<template>
  <!--
    CaseSummaryBar.vue - Top-bjælke med sags-overblik
    ==================================================
    Viser en live-opdateret oversigt over sagens økonomi.
    Erstatter den gamle header med "Tilføj Sag / Annuller".

    Viser:
    - Hovedstol (sum af fakturaer minus kreditnotaer)
    - Rykkergebyrer (sum, max 100 kr/stk)
    - Kompensationsgebyr (310 kr hvis sat)
    - Renter (sum fra alle rykkere)
    - Total (alt samlet)
  -->
  <div class="case-summary-bar">
    <div class="summary-items">
      <!-- Hovedstol -->
      <div class="summary-item">
        <div class="item-label">
          <feather-icon
            icon="FileTextIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('DEBTIA_PRINCIPAL') }}
        </div>
        <div class="item-value">
          {{ formatAmount(hovedstol) }} kr
        </div>
      </div>

      <!-- Rykkergebyrer -->
      <div
        v-if="totalReminderFee > 0"
        class="summary-item"
      >
        <div class="item-label">
          <feather-icon
            icon="AlertCircleIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('DEBTIA_CREATE_SUMMARY_REMINDER_FEES') }}
        </div>
        <div class="item-value text-info">
          +{{ formatAmount(totalReminderFee) }} kr
        </div>
      </div>

      <!-- Kompensationsgebyr -->
      <div
        v-if="totalCompensationFee > 0"
        class="summary-item"
      >
        <div class="item-label">
          <feather-icon
            icon="ShieldIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('DEBTIA_CREATE_SUMMARY_COMPENSATION') }}
        </div>
        <div class="item-value text-info">
          +{{ formatAmount(totalCompensationFee) }} kr
        </div>
      </div>

      <!-- Renter -->
      <div
        v-if="totalInterestAmount > 0"
        class="summary-item"
      >
        <div class="item-label">
          <feather-icon
            icon="PercentIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('DEBTIA_INTEREST') }}
        </div>
        <div class="item-value text-info">
          +{{ formatAmount(totalInterestAmount) }} kr
        </div>
      </div>

      <!-- Betalt -->
      <div
        v-if="totalPaid > 0"
        class="summary-item"
      >
        <div class="item-label">
          <feather-icon
            icon="CheckCircleIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('DEBTIA_STATUS_PAID') }}
        </div>
        <div class="item-value text-success">
          -{{ formatAmount(totalPaid) }} kr
        </div>
      </div>

      <!-- Separator -->
      <div class="summary-separator" />

      <!-- Total -->
      <div class="summary-item total">
        <div class="item-label">
          <feather-icon
            icon="TrendingUpIcon"
            size="16"
            class="mr-25"
          />
          {{ $t('DEBTIA_TOTAL') }}
        </div>
        <div class="item-value">
          {{ formatAmount(total - totalPaid) }} kr
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import { useCaseCreation } from '../composables/useCaseCreation'

export default {
  name: 'CaseSummaryBar',

  setup() {
    const {
      state,
      totalReminderFee,
      totalCompensationFee,
      totalInterestAmount,
    } = useCaseCreation()

    // Beregn hovedstol (fakturaer minus kreditnotaer)
    const hovedstol = computed(() => {
      // Sum af alle grundlag-dokumenters beløb
      const fakturaSum = state.documents.grundlag.reduce(
        (sum, doc) => sum + (parseFloat(doc.paymentAmount) || 0),
        0,
      )

      // Sum af kreditnotaer (fratrækkes)
      const kreditnotaSum = state.documents.kreditnoter.reduce(
        (sum, doc) => sum + (parseFloat(doc.creditNoteAmount) || 0),
        0,
      )

      return Math.max(0, fakturaSum - kreditnotaSum)
    })

    // Beregn samlet betalt
    const totalPaid = computed(() => state.documents.grundlag.reduce(
      (sum, doc) => sum + (parseFloat(doc.amountPaid) || 0),
      0,
    ))

    // Beregn total (brutto, før betalt)
    const total = computed(() => hovedstol.value
        + totalReminderFee.value
        + totalCompensationFee.value
        + totalInterestAmount.value)

    // Formatér beløb med tusindtalsseparator
    function formatAmount(amount) {
      if (amount === null || amount === undefined) return '0'
      return new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)
    }

    return {
      hovedstol,
      totalReminderFee,
      totalCompensationFee,
      totalInterestAmount,
      totalPaid,
      total,
      formatAmount,
    }
  },
}
</script>

<style lang="scss" scoped>
// Debtia farver
$debtia-green: #088c76;
$debtia-blue: #002954;
$debtia-blue-light: #0c3860;

.case-summary-bar {
  background: linear-gradient(135deg, $debtia-green 0%, $debtia-blue 50%, $debtia-blue-light 100%);
  border-radius: 0.5rem;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(8, 140, 118, 0.3);
}

.summary-items {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;

  .item-label {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
  }

  .item-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #fff;

    &.text-info {
      color: #a8edea;
    }

    &.text-success {
      color: #28c76f;
    }
  }

  &.total {
    .item-label {
      font-size: 0.85rem;
      color: #fff;
    }

    .item-value {
      font-size: 1.5rem;
      font-weight: 700;
    }
  }
}

.summary-separator {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 0.5rem;
}

// Responsive
@media (max-width: 768px) {
  .case-summary-bar {
    padding: 0.75rem 1rem;
  }

  .summary-items {
    justify-content: center;
  }

  .summary-item {
    min-width: 80px;

    .item-label {
      font-size: 0.65rem;
    }

    .item-value {
      font-size: 0.95rem;
    }

    &.total {
      .item-value {
        font-size: 1.2rem;
      }
    }
  }

  .summary-separator {
    display: none;
  }
}
</style>
